import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { getServices } from '../services/servicesApi';
import { getArticles, formatDuration } from '../services/articlesApi';
import TopRatedDoctors from '../components/TopRatedDoctors';
import { Icon, PageHero, Pill, SectionHeader } from '../components/ui';

/* ─── Service card ──────────────────────────────────────────────── */
const SkeletonTile = () => (
  <div className="ds-service">
    <div className="ds-service-media skeleton" />
    <div className="ds-service-body">
      <div className="skeleton" style={{ width: '55%', height: 15 }} />
      <div className="skeleton" style={{ width: '90%', height: 11 }} />
      <div className="skeleton" style={{ width: '70%', height: 11 }} />
      <div className="skeleton" style={{ width: '100%', height: 34, borderRadius: 10, marginTop: 12 }} />
    </div>
  </div>
);

const ServiceTile = ({ service }) => {
  const navigate = useNavigate();
  const enabled = service.available && service.route;
  const handleClick = () => {
    if (!enabled) return;
    navigate(service.route);
  };
  return (
    <div
      className={`ds-service${service.available ? '' : ' is-unavailable'}`}
      style={{ '--svc-bg': service.bg, '--svc-accent': service.accent, cursor: enabled ? 'pointer' : 'default' }}
      onClick={handleClick}
    >
      {!service.available && <div className="ds-service-soon">Coming soon</div>}
      <div className="ds-service-media" aria-hidden="true">
        <span className="ds-service-emoji">{service.icon}</span>
      </div>
      <div className="ds-service-body">
        <h3 className="ds-service-title">{service.title}</h3>
        <p className="ds-service-desc">{service.description}</p>
        {service.available && (
          <button
            type="button"
            className="ds-btn ds-btn--sm ds-btn--block"
            disabled={!enabled}
            onClick={(e) => { e.stopPropagation(); handleClick(); }}
          >
            {service.cta || 'Explore'} <Icon name="arrowRight" size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

/* ─── Health Insights section ────────────────────────────────────── */
const TYPE_META = {
  ARTICLE:    { label: 'Article',     icon: '📄', color: '#4f46e5' },
  VIDEO:      { label: 'Video',       icon: '🎬', color: '#dc2626' },
  WHITEPAPER: { label: 'White Paper', icon: '📋', color: '#0891b2' },
};

const INSIGHT_FILTERS = [
  { value: 'ALL',        label: 'All' },
  { value: 'ARTICLE',    label: 'Articles' },
  { value: 'VIDEO',      label: 'Videos' },
  { value: 'WHITEPAPER', label: 'White Papers' },
];

const InsightSkeletonCard = () => (
  <div className="insight-card" style={{ cursor: 'default' }}>
    <div className="skeleton insight-card__thumb" />
    <div className="insight-card__body" style={{ gap: 8 }}>
      <div className="skeleton skeleton-line" style={{ width: '28%', height: 16, borderRadius: 9999 }} />
      <div className="skeleton skeleton-line" style={{ width: '88%', height: 13 }} />
      <div className="skeleton skeleton-line" style={{ width: '65%', height: 11 }} />
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 'auto' }}>
        <div className="skeleton" style={{ width: 24, height: 24, borderRadius: '50%' }} />
        <div className="skeleton skeleton-line" style={{ width: '40%', height: 11 }} />
      </div>
    </div>
  </div>
);

const InsightCard = ({ item, onClick }) => {
  const meta = TYPE_META[item.type] || TYPE_META.ARTICLE;

  const dateStr = item.publishedDate
    ? new Date(item.publishedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  const viewsStr = item.viewCount >= 1000
    ? `${(item.viewCount / 1000).toFixed(1)}k`
    : String(item.viewCount ?? 0);

  return (
    <div className="insight-card" onClick={onClick}>
      {/* Thumbnail */}
      <div className="insight-card__thumb">
        {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" />}

        {/* Type badge overlay */}
        <span className="insight-card__type-badge" style={{ color: meta.color }}>
          {meta.icon} {meta.label}
        </span>

        {/* Video overlay */}
        {item.type === 'VIDEO' && (
          <div className="insight-card__play">
            <div className="insight-card__play-btn">
              <svg width="13" height="15" viewBox="0 0 13 15" fill="#1e1b4b">
                <path d="M1 1l11 6.5L1 14V1z"/>
              </svg>
            </div>
            {item.videoDurationSecs && (
              <span className="insight-card__duration">
                {formatDuration(item.videoDurationSecs)}
              </span>
            )}
          </div>
        )}

        {/* Read time for non-video */}
        {item.type !== 'VIDEO' && item.readTimeMinutes && (
          <span className="insight-card__duration">
            {item.readTimeMinutes} min read
          </span>
        )}
      </div>

      {/* Body */}
      <div className="insight-card__body">
        <div className="insight-card__title">
          {item.title}
        </div>

        <p className="insight-card__summary">{item.summary}</p>

        {/* Doctor + date + views */}
        <div className="insight-card__footer">
          <img
            src={item.doctorPhoto || `https://api.dicebear.com/7.x/personas/svg?seed=${item.doctorId}`}
            alt=""
            className="insight-card__avatar"
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="insight-card__doc-name">{item.doctorName}</div>
            <div className="insight-card__doc-dept">
              {item.department}{dateStr ? ` · ${dateStr}` : ''}
            </div>
          </div>
          {viewsStr && (
            <span className="insight-card__views">👁 {viewsStr}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const HealthInsightsSection = ({ tenantId }) => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('ALL');
  const [showAll, setShowAll]   = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    getArticles(tenantId)
      .then(data => { setArticles(data); setLoading(false); })
      .catch(() => { setArticles([]); setLoading(false); });
  }, [tenantId]);

  if (!loading && articles.length === 0) return null;

  const counts = {
    ALL:        articles.length,
    ARTICLE:    articles.filter(a => a.type === 'ARTICLE').length,
    VIDEO:      articles.filter(a => a.type === 'VIDEO').length,
    WHITEPAPER: articles.filter(a => a.type === 'WHITEPAPER').length,
  };

  const filtered = filter === 'ALL' ? articles : articles.filter(a => a.type === filter);
  const visible  = showAll ? filtered : filtered.slice(0, 6);

  return (
    <div className="fade-up-2 ds-section">
      <SectionHeader
        title="Health Insights"
        subtitle="Articles, videos, and research from our doctors"
        onViewAll={!loading && filtered.length > 6 ? () => setShowAll(v => !v) : undefined}
        viewAllLabel={showAll ? 'Show less' : 'View all'}
      />

      {/* Filter pills */}
      {!loading && articles.length > 0 && (
        <div className="pill-filter-row">
          {INSIGHT_FILTERS.filter(f => f.value === 'ALL' || counts[f.value] > 0).map(f => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                className={`pill-filter${active ? ' pill-filter--active' : ''}`}
                onClick={() => { setFilter(f.value); setShowAll(false); }}
              >
                {f.label}
                {counts[f.value] > 0 && (
                  <span className="pill-filter__count">{counts[f.value]}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Grid — equal-height cards */}
      <div className="insight-grid">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <InsightSkeletonCard key={i} />)
          : visible.map((item) => (
            <InsightCard
              key={item.id}
              item={item}
              onClick={() => navigate(`/articles/${item.id}`)}
            />
          ))
        }
      </div>

      {/* Show more */}
      {!loading && !showAll && filtered.length > 6 && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button
            className="btn-outline"
            onClick={() => setShowAll(true)}
          >
            Show {filtered.length - 6} more →
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Main Home component ────────────────────────────────────────── */
const Home = () => {
  const { tenant } = useTenant();
  const [services, setServices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    if (!tenant?.id) return;
    setLoading(true);
    setError(null);
    getServices(tenant.id)
      .then(data => { setServices(data); setLoading(false); })
      .catch(() => { setError('Could not load services.'); setLoading(false); });
  }, [tenant?.id]);

  const heroTitle = <>Your health, <span className="gradient-text">all in one place</span></>;

  if (!tenant) return (
    <div className="page">
      <main className="page-main">
        <PageHero
          size="xl" tall art="wellness"
          title={heroTitle}
          subtitle="Select a hospital or clinic from the top bar to get started."
        />
      </main>
    </div>
  );

  return (
    <div className="page">
      <main className="page-main">

        <PageHero
          size="xl" tall art="wellness"
          badge={tenant?.name && <Pill tone="indigo">{tenant.name}</Pill>}
          title={heroTitle}
          subtitle="Access pharmacy, consultations, lab tests, and more — seamlessly connected for you and your family."
        />

        {/* Services grid */}
        <div className="fade-up-1">
          <SectionHeader
            title="Our Services"
            meta={!loading && !error && `${services.filter(s => s.available).length} available`}
          />

          {error ? (
            <div className="error-state">
              <span>⚠️</span> {error}
            </div>
          ) : (
            <div className="ds-service-grid">
              {loading
                ? Array.from({ length: 8 }).map((_, i) => <SkeletonTile key={i} />)
                : services.map((s, i) => (
                    <div key={s.id} style={{ animation: `fadeUp .4s ${i * 50}ms ease both` }}>
                      <ServiceTile service={s} />
                    </div>
                  ))
              }
            </div>
          )}
        </div>

        {/* Top Rated Doctors */}
        <div className="fade-up-2 ds-section">
          <TopRatedDoctors limit={3} />
        </div>

        {/* Health Insights */}
        <HealthInsightsSection tenantId={tenant?.id} />

      </main>
    </div>
  );
};

export default Home;